/**
 * Workspace — §6 Unified trading workspace with 8 hotspots.
 *
 * A large mock workspace panel with 8 clickable hotspot markers.
 * Clicking a hotspot pops up a small tooltip explaining that panel
 * and its role.  Keyboard-accessible.
 *
 * 06/2026 — Enriched the mock canvas so the section no longer looks
 * empty on first paint: added realistic panel titles, a mini chart,
 * a scanner list, an order-book strip, an AI Mentor chat bubble and
 * a P&L KPI row — all pure SVG / CSS, no heavy dependencies.
 */
import React, { useState } from 'react'
import clsx from 'clsx'
import {
  ScanLine, LineChart, Zap, Wallet, PieChart, Wand2, Bot, BellRing,
  Sparkles, X, TrendingUp, TrendingDown, ArrowUpRight, ArrowDownRight,
} from 'lucide-react'
import { Container, Eyebrow, SectionHeading } from '@/components/landing/design-system'
import { WORKSPACE } from '../data'
import { TONE } from '../_shared'

const ICONS = { ScanLine, LineChart, Zap, Wallet, PieChart, Wand2, Bot, BellRing }

// Tiny inline SVG sparkline generator (deterministic seed for SSR-safe render).
function Sparkline({ w = 220, h = 60, color = '#FFC107', accent = 'rgba(255,193,7,0.20)' }) {
  const seeds = [45, 42, 48, 46, 52, 50, 55, 58, 54, 60, 62, 66, 64, 70, 74, 72, 78]
  const max = Math.max(...seeds)
  const min = Math.min(...seeds)
  const step = w / (seeds.length - 1)
  const points = seeds.map((v, i) => {
    const x = i * step
    const y = h - ((v - min) / (max - min)) * (h - 6) - 3
    return `${x.toFixed(1)},${y.toFixed(1)}`
  }).join(' ')
  const fillPoints = `0,${h} ${points} ${w},${h}`
  return (
    <svg viewBox={`0 0 ${w} ${h}`} className="w-full h-full">
      <polygon points={fillPoints} fill={accent} />
      <polyline points={points} fill="none" stroke={color} strokeWidth="1.5" />
    </svg>
  )
}

function OrderBook() {
  const bids = [
    { p: '65,412.30', s: '2.140', t: 'green' },
    { p: '65,410.85', s: '1.820', t: 'green' },
    { p: '65,409.10', s: '3.240', t: 'green' },
    { p: '65,407.50', s: '0.960', t: 'green' },
  ]
  const asks = [
    { p: '65,414.20', s: '1.740', t: 'red' },
    { p: '65,415.75', s: '2.560', t: 'red' },
    { p: '65,417.90', s: '0.880', t: 'red' },
    { p: '65,419.40', s: '3.020', t: 'red' },
  ]
  return (
    <div className="flex flex-col text-[9.5px] font-mono leading-tight">
      {asks.reverse().map((r, i) => (
        <div key={`a${i}`} className="flex justify-between px-2 py-[1px] text-red-400/90">
          <span>{r.p}</span><span className="text-red-400/60">{r.s}</span>
        </div>
      ))}
      <div className="flex justify-between px-2 py-[3px] my-0.5 rounded-sm bg-white/[0.06] text-white font-semibold">
        <span>65,413.05</span><span className="text-emerald-400">▲ 0.24%</span>
      </div>
      {bids.map((r, i) => (
        <div key={`b${i}`} className="flex justify-between px-2 py-[1px] text-emerald-400/90">
          <span>{r.p}</span><span className="text-emerald-400/60">{r.s}</span>
        </div>
      ))}
    </div>
  )
}

export default function Workspace() {
  const [activeId, setActiveId] = useState(null)
  const active = WORKSPACE.find((s) => s.id === activeId)
  const tone = TONE[active?.tone || 'orange']
  return (
    <section id="trading-workspace" className="relative py-24 md:py-28">
      <Container size="wide" className="flex flex-col gap-10">
        <div className="flex flex-col items-center text-center gap-5 max-w-3xl mx-auto">
          <Eyebrow>Unified Trading Workspace</Eyebrow>
          <SectionHeading align="center"
            title={<>Every trading activity, <span className="bg-gradient-to-r from-brand-gold to-[#FFE17A] bg-clip-text text-transparent">one workspace.</span></>}
            subtitle="A single canvas: scanner, charts, order flow, positions, P&L and AI mentor — all wired together. Click any hotspot to see how each surface plays in the trading day."
          />
        </div>

        <div className="relative rounded-3xl border border-white/10 bg-gradient-to-br from-white/[0.03] to-white/[0.01] p-4 md:p-6 backdrop-blur-xl overflow-hidden">
          <div className="pointer-events-none absolute inset-0 opacity-60"
            style={{ background: 'radial-gradient(60% 40% at 30% 0%, rgba(34,197,94,0.10), transparent 70%)' }} />

          {/* Browser chrome */}
          <div className="relative flex items-center gap-1.5 rounded-md bg-black/40 px-2.5 py-2 border border-white/5 mb-3">
            <div className="h-2 w-2 rounded-full bg-red-500/70" />
            <div className="h-2 w-2 rounded-full bg-amber-500/70" />
            <div className="h-2 w-2 rounded-full bg-emerald-500/70" />
            <div className="ml-3 flex-1 h-5 rounded-md bg-white/[0.04] flex items-center px-2 text-[10px] font-mono text-zinc-500">
              app.kalkiai.trade/workspace
            </div>
            <div className="text-[10px] font-mono text-emerald-400 pr-1">• live</div>
          </div>

          {/* KPI strip */}
          <div className="relative grid grid-cols-2 md:grid-cols-4 gap-2 mb-3">
            {[
              { l: 'Portfolio', v: '$482,140', d: '+2.14%', up: true },
              { l: 'Day P&L',   v: '+$8,924',  d: 'Δ intraday', up: true },
              { l: 'Open pos.', v: '14',       d: '3 hedged',   up: true },
              { l: 'Win-rate',  v: '61.4%',    d: '+0.9 pp',    up: true },
            ].map((k) => (
              <div key={k.l} className="rounded-md border border-white/10 bg-white/[0.03] px-3 py-2">
                <div className="flex items-center justify-between">
                  <div className="text-[9.5px] uppercase tracking-wider font-mono text-zinc-500">{k.l}</div>
                  <div className={clsx('inline-flex items-center gap-0.5 text-[9.5px] font-mono', k.up ? 'text-emerald-400' : 'text-red-400')}>
                    {k.up ? <TrendingUp className="h-2.5 w-2.5" /> : <TrendingDown className="h-2.5 w-2.5" />}
                    {k.d}
                  </div>
                </div>
                <div className="text-base md:text-lg font-semibold text-white mt-0.5 tabular-nums">{k.v}</div>
              </div>
            ))}
          </div>

          {/* Grid mock — 3 columns × 2 rows of mini panels behind hotspots.
              Hidden on mobile (<md) where the density becomes unreadable;
              mobile users get a cleaner placeholder canvas that still
              renders the hotspots on top. */}
          <div className="relative w-full aspect-[16/10] rounded-xl border border-white/5 bg-black/30 overflow-hidden">
            <div className="hidden md:grid absolute inset-0 grid-cols-12 grid-rows-6 gap-2 p-3 text-[10px]">
              {/* Row 1 — Watchlist */}
              <div className="col-span-3 row-span-3 rounded-md border border-white/5 bg-white/[0.02] p-2 overflow-hidden">
                <div className="flex items-center justify-between mb-1.5">
                  <div className="font-mono uppercase tracking-wider text-orange-400/80">Watchlist</div>
                  <div className="text-zinc-500 font-mono">08 · live</div>
                </div>
                {[
                  { s: 'BTCUSD',  p: '65,413', d: '+1.42%',  up: true },
                  { s: 'ETHUSD',  p: '3,412',  d: '+0.86%',  up: true },
                  { s: 'AAPL',    p: '224.18', d: '+0.34%',  up: true },
                  { s: 'MES1!',   p: '5,842',  d: '−0.18%',  up: false },
                  { s: 'NQ1!',    p: '20,142', d: '+0.94%',  up: true },
                  { s: 'GC1!',    p: '2,384',  d: '+0.42%',  up: true },
                ].map((r) => (
                  <div key={r.s} className="flex items-center justify-between py-0.5 border-b border-white/[0.03] last:border-0">
                    <span className="text-zinc-300 font-mono">{r.s}</span>
                    <span className="text-zinc-500 font-mono">{r.p}</span>
                    <span className={clsx('font-mono text-[9px]', r.up ? 'text-emerald-400' : 'text-red-400')}>{r.d}</span>
                  </div>
                ))}
              </div>

              {/* Charts (center wide) */}
              <div className="col-span-6 row-span-3 rounded-md border border-white/5 bg-gradient-to-br from-emerald-500/[0.06] to-transparent p-2 relative overflow-hidden">
                <div className="flex items-center justify-between mb-1">
                  <div className="font-mono uppercase tracking-wider text-emerald-400/80">BTC/USD · 4H</div>
                  <div className="flex items-center gap-1 text-zinc-500 font-mono">
                    {['1H','4H','1D','1W'].map((tf) => (
                      <span key={tf} className={clsx('px-1 py-0.5 rounded', tf === '4H' ? 'bg-emerald-500/15 text-emerald-400' : '')}>{tf}</span>
                    ))}
                  </div>
                </div>
                <div className="w-full h-[calc(100%-24px)] pt-1">
                  <Sparkline w={520} h={130} color="#22c55e" accent="rgba(34,197,94,0.16)" />
                </div>
                {/* floating price */}
                <div className="absolute right-3 bottom-3 rounded-md border border-emerald-400/25 bg-black/60 backdrop-blur px-2 py-1 text-[10px] text-emerald-300 font-mono">
                  65,413.05 <span className="text-emerald-400">+1.42%</span>
                </div>
              </div>

              {/* Order Panel (right) */}
              <div className="col-span-3 row-span-3 rounded-md border border-white/5 bg-white/[0.02] p-2 overflow-hidden">
                <div className="flex items-center justify-between mb-1.5">
                  <div className="font-mono uppercase tracking-wider text-orange-400/80">Order Book</div>
                  <div className="text-zinc-500 font-mono">DOM</div>
                </div>
                <OrderBook />
              </div>

              {/* Row 2 — Positions */}
              <div className="col-span-3 row-span-3 rounded-md border border-white/5 bg-white/[0.02] p-2">
                <div className="flex items-center justify-between mb-1.5">
                  <div className="font-mono uppercase tracking-wider text-emerald-400/80">Positions</div>
                  <div className="text-zinc-500 font-mono">14 open</div>
                </div>
                {[
                  { s: 'BTC',  q: '+0.42', pnl: '+$1,842', up: true },
                  { s: 'ETH',  q: '+4.10', pnl: '+$342',   up: true },
                  { s: 'AAPL', q: '−120',  pnl: '−$186',   up: false },
                  { s: 'MES',  q: '+2',    pnl: '+$620',   up: true },
                  { s: 'NQ',   q: '−1',    pnl: '+$920',   up: true },
                ].map((r) => (
                  <div key={r.s} className="flex items-center justify-between py-0.5 border-b border-white/[0.03] last:border-0 text-[9.5px] font-mono">
                    <span className="text-zinc-300">{r.s}</span>
                    <span className="text-zinc-500">{r.q}</span>
                    <span className={clsx(r.up ? 'text-emerald-400' : 'text-red-400')}>{r.pnl}</span>
                  </div>
                ))}
              </div>

              {/* Portfolio pie / donut summary */}
              <div className="col-span-3 row-span-3 rounded-md border border-white/5 bg-white/[0.02] p-2">
                <div className="font-mono uppercase tracking-wider text-blue-400/80 mb-1.5">Portfolio Mix</div>
                <div className="flex items-center gap-2 h-full">
                  <svg viewBox="0 0 42 42" className="h-14 w-14 shrink-0" aria-hidden>
                    <circle cx="21" cy="21" r="15.915" fill="transparent" stroke="rgba(255,255,255,0.05)" strokeWidth="6" />
                    <circle cx="21" cy="21" r="15.915" fill="transparent" stroke="#22c55e" strokeWidth="6" strokeDasharray="42 100" strokeDashoffset="25" />
                    <circle cx="21" cy="21" r="15.915" fill="transparent" stroke="#3b82f6" strokeWidth="6" strokeDasharray="28 100" strokeDashoffset="-17" />
                    <circle cx="21" cy="21" r="15.915" fill="transparent" stroke="#a855f7" strokeWidth="6" strokeDasharray="18 100" strokeDashoffset="-45" />
                    <circle cx="21" cy="21" r="15.915" fill="transparent" stroke="#FFC107" strokeWidth="6" strokeDasharray="12 100" strokeDashoffset="-63" />
                  </svg>
                  <div className="flex flex-col gap-0.5 text-[9.5px] font-mono">
                    <span className="text-emerald-400">■ Crypto 42%</span>
                    <span className="text-blue-400">■ Equities 28%</span>
                    <span className="text-purple-400">■ Futures 18%</span>
                    <span className="text-orange-400">■ Cash 12%</span>
                  </div>
                </div>
              </div>

              {/* Strategy status */}
              <div className="col-span-3 row-span-3 rounded-md border border-white/5 bg-white/[0.02] p-2">
                <div className="flex items-center justify-between mb-1.5">
                  <div className="font-mono uppercase tracking-wider text-purple-400/80">Strategy · Live</div>
                  <div className="text-emerald-400 font-mono">RUNNING</div>
                </div>
                {[
                  { n: 'BB-Squeeze',      t: '61%', up: true },
                  { n: 'Mean-Reversion',  t: '58%', up: true },
                  { n: 'Vol-Compression', t: '47%', up: false },
                  { n: 'Momentum-Break',  t: '69%', up: true },
                ].map((r) => (
                  <div key={r.n} className="flex items-center justify-between py-0.5 border-b border-white/[0.03] last:border-0 text-[9.5px] font-mono">
                    <span className="text-zinc-300">{r.n}</span>
                    <span className={clsx(r.up ? 'text-emerald-400' : 'text-red-400')}>{r.t}</span>
                  </div>
                ))}
              </div>

              {/* AI Mentor bubble (spans right column) */}
              <div className="col-span-3 row-span-3 rounded-md border border-orange-400/20 bg-orange-400/[0.05] p-2 flex flex-col">
                <div className="flex items-center gap-1.5 mb-1">
                  <div className="h-5 w-5 rounded-md bg-brand-gold/15 text-brand-gold flex items-center justify-center">
                    <Bot className="h-3 w-3" />
                  </div>
                  <div className="font-mono uppercase tracking-wider text-orange-400/90">AI Mentor</div>
                  <div className="ml-auto text-emerald-400 font-mono">live</div>
                </div>
                <div className="rounded-md bg-white/[0.04] px-1.5 py-1 text-[9.5px] text-zinc-300 leading-snug">
                  &ldquo;Why is BTC bullish today?&rdquo;
                </div>
                <div className="mt-1 rounded-md border border-orange-400/25 bg-orange-400/[0.08] px-1.5 py-1 text-[9.5px] text-zinc-200 leading-snug">
                  BTC broke 4H resistance at $63.4k with rising volume. OB held. <span className="text-brand-gold">Confidence 74%</span>.
                </div>
                <div className="mt-auto pt-1 text-[8.5px] text-zinc-500 font-mono">
                  <ArrowUpRight className="h-2.5 w-2.5 inline text-emerald-400" /> Cites: HTF · 4H · vol
                </div>
              </div>
            </div>

            {/* Mobile placeholder — clean brand mark inside a
                simulated screen so the canvas doesn't look empty
                when the 6-panel grid is hidden. */}
            <div className="md:hidden absolute inset-0 flex items-center justify-center">
              <div className="flex flex-col items-center gap-3 text-center px-6">
                <div className="inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-gradient-to-br from-brand-gold to-[#FFE17A] shadow-[0_10px_40px_-10px_rgba(255,193,7,0.55)]">
                  <Sparkles className="h-6 w-6 text-black" />
                </div>
                <div className="text-[13px] font-mono uppercase tracking-widest text-zinc-400">
                  Unified workspace
                </div>
                <div className="text-[11.5px] text-zinc-500 leading-relaxed max-w-[240px]">
                  Tap any hotspot below to see how each surface plays in the trading day.
                </div>
              </div>
            </div>

            {/* Hotspots — hidden on mobile where positional density
                creates overlap issues.  Mobile users get a clean
                tap-list below the canvas instead. */}
            {WORKSPACE.map((s) => {
              const t = TONE[s.tone] || TONE.orange
              const Icon = ICONS[s.iconKey] || Sparkles
              const isActive = activeId === s.id
              return (
                <button
                  key={s.id}
                  onClick={() => setActiveId(isActive ? null : s.id)}
                  aria-label={s.name}
                  className={clsx(
                    'group hidden md:flex absolute -translate-x-1/2 -translate-y-1/2 focus-visible:outline-none',
                    'items-center gap-2 rounded-full border pl-1.5 pr-3 py-1 transition-all',
                    isActive ? 'z-20 border-white/30 bg-black/85 shadow-[0_10px_30px_-10px_rgba(0,0,0,0.9)]'
                             : 'border-white/15 bg-black/70 hover:bg-black/85',
                  )}
                  style={{ ...s.pos, color: t.hex }}
                >
                  <span className="inline-flex h-6 w-6 items-center justify-center rounded-full"
                    style={{ background: `rgba(${t.rgb},0.16)`, border: `1px solid rgba(${t.rgb},0.45)` }}>
                    <Icon strokeWidth={2} style={{ width: 12, height: 12 }} />
                  </span>
                  <span className="text-[11px] font-semibold text-white whitespace-nowrap">{s.name}</span>
                  <span className="inline-flex h-2 w-2 rounded-full animate-ping absolute -top-0.5 -right-0.5" style={{ background: t.hex }} />
                </button>
              )
            })}
          </div>

          {/* Mobile tap-list of hotspots — replaces the positional
              hotspot chips (which are hidden on <md) with a clean
              two-column grid immediately below the canvas. */}
          <div className="md:hidden mt-4 grid grid-cols-2 gap-2">
            {WORKSPACE.map((s) => {
              const t = TONE[s.tone] || TONE.orange
              const Icon = ICONS[s.iconKey] || Sparkles
              const isActive = activeId === s.id
              return (
                <button
                  key={s.id}
                  onClick={() => setActiveId(isActive ? null : s.id)}
                  aria-label={s.name}
                  className={clsx(
                    'flex items-center gap-2 rounded-xl border pl-2 pr-3 py-2 text-left transition-all',
                    isActive ? 'border-white/30 bg-white/[0.06]'
                             : 'border-white/10 bg-white/[0.02] hover:bg-white/[0.04]',
                  )}
                >
                  <span className="inline-flex h-7 w-7 items-center justify-center rounded-full shrink-0"
                    style={{ background: `rgba(${t.rgb},0.16)`, border: `1px solid rgba(${t.rgb},0.45)`, color: t.hex }}>
                    <Icon strokeWidth={2} style={{ width: 14, height: 14 }} />
                  </span>
                  <span className="text-[12.5px] font-semibold text-white leading-tight">{s.name}</span>
                </button>
              )
            })}
          </div>

          {/* Tooltip / detail card */}
          {active && (
            <div className="mt-4 rounded-2xl border border-white/15 bg-black/60 backdrop-blur-xl p-4 flex items-start gap-3">
              <span className="inline-flex h-9 w-9 items-center justify-center rounded-lg border shrink-0"
                style={{ borderColor: `rgba(${tone.rgb},0.35)`, background: `rgba(${tone.rgb},0.08)`, color: tone.hex }}>
                <Sparkles style={{ width: 15, height: 15 }} strokeWidth={1.8} />
              </span>
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <div className="text-base font-semibold text-white leading-tight">{active.name}</div>
                </div>
                <p className="text-sm text-zinc-300 leading-relaxed mt-1">{active.body}</p>
              </div>
              <button onClick={() => setActiveId(null)} className="text-zinc-500 hover:text-white" aria-label="Close hotspot">
                <X className="h-4 w-4" />
              </button>
            </div>
          )}
        </div>
      </Container>
    </section>
  )
}
